import dayjs from 'dayjs'
import { TResumeSettingItem } from '../store'

const customParseFormat = require('dayjs/plugin/customParseFormat')
dayjs.extend(customParseFormat)

export function formatMmYyyy(date: Date): string {
    return dayjs(date).format('MM/YYYY')
}

export function formatDdMmYyyy(date: Date): string {
    return dayjs(date).format('DD/MM/YYYY')
}

export function formatYyyyMmDd(date: Date): string {
    return dayjs(date).format('YYYY-MM-DD')
}

export function dateToString(date: Date, format: string): string {
    return dayjs(date).format(format)
}

type TUnit = 'month' | 'year'

export function dateDiff(fromDate: Date, toDate: Date, unit: TUnit = 'month') {
    const fromDateObj = dayjs(formatYyyyMmDd(fromDate))
    const toDateObj = dayjs(formatYyyyMmDd(toDate))

    // We need to format Date obj to YYYY-MM-DD before "diff" to make it compare only date not by time

    return toDateObj.diff(fromDateObj, unit)
}

export function parseFromString(dateStr: string, format?: string) {
    return dayjs(dateStr, format).toDate()
}

export function isSame(date1: Date, date2: Date) {
    return dayjs(dayjs(date1)).isSame(dayjs(date2))
}

export const EMPTY_DATE = '0000-00-00'

/**
 * We need to do this because the date value from DatePicker is sometimes one day off in the past
 * @see https://github.com/Hacker0x01/react-datepicker/issues/1018
 */
export function offsetDate(date: Date) {
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
}

/**
 * Convert GMT+7 to UTC
 * @param {number} timestamp from API server (Ex: 1638958392)
 * @return string dd-mm-yyyy
 * API return timestamp in GMT+7 this means we have to convert it to UTC.
 * If not convert then time will be +7 hours (Not correct time save in database)
 */
export function formatDateFromGMT(timestamp) {
    const date = new Date(timestamp * 1000)
    const rawDay = date.getUTCDate()
    // Month is from 0 to 11
    const rawMonth = date.getUTCMonth() + 1
    const day = rawDay < 10 ? `0${rawDay}` : rawDay
    const month = rawMonth < 10 ? `0${rawMonth}` : rawMonth
    return day + '/' + month + '/' + date.getUTCFullYear()
}

const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const months = [
    '',
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
]

const nth = (d: number) => {
    if (d > 3 && d < 21) return 'th'
    switch (d % 10) {
        case 1:
            return 'st'
        case 2:
            return 'nd'
        case 3:
            return 'rd'
        default:
            return 'th'
    }
}

export const convertDateTime = (time: any, type = 0) => {
    const date = new Date(time)
    const day = date.getDate()
    const dayName = days[date.getDay()]
    const month = date.getMonth() + 1
    const monthName = months[month]
    const year = date.getFullYear()
    const hours = date.getHours()
    const minutes = date.getMinutes()
    const seconds = date.getSeconds()

    switch (type) {
        case 0: {
            // 30/10/2022 07:30
            const formatDate = String(day).padStart(2, '0') + '/' + String(month).padStart(2, '0') + '/' + year
            const formatTime = String(hours).padStart(2, '0') + ':' + String(minutes).padStart(2, '0')
            return `${formatDate} ${formatTime}`
        }
        case 1: {
            // Friday, 15 Oct 2022
            return `${dayName}, ${day} ${monthName} ${year}`
        }
        case 2: {
            // Oct 5th, 2022
            return `${monthName} ${day}${nth(day)}, ${year}`
        }
        case 3: {
            // 06:30
            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
        }
        case 4: {
            // 15 Wed, 2022
            return `${day} ${monthName.slice(0, 3)}, ${year}`
        }
        case 5: {
            // Wed 15, 2022
            return `${monthName.slice(0, 3)} ${day}, ${year}`
        }
        case 6: {
            // 2022-10-18 11:04:20
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
        }
        case 7: {
            // 30/10/2022
            const formatDate = String(day).padStart(2, '0') + '/' + String(month).padStart(2, '0') + '/' + year
            return formatDate
        }
        case 8: {
            // 04:20
            return `${minutes}:${seconds}`
        }
        case 9: {
            // 2022-10-18 11:04:20
            return `${month}/${year}`
        }
        case 10: {
            // 2022-10-18 11:04:20
            return `${year}`
        }
        default:
            return ''
    }
}

export function datetimeFormat(date: Date, locale?: string, options?: Intl.DateTimeFormatOptions): string {
    const newDate = date
    const formatter = new Intl.DateTimeFormat(locale ?? 'vi-VN', options ?? { dateStyle: 'short' })
    return formatter.format(newDate)
}

export function getLastUpdatedAttachment(attachInfo: TResumeSettingItem[]): TResumeSettingItem | null {
    if (attachInfo.length === 0) {
      // Handle the case where the array is empty
      return null
    }
    // Initialize maxItem with the first item in the array
    let maxItem = attachInfo[0]

    for (let i = 1; i < attachInfo.length; i++) {
        if (attachInfo[i].updatedDate > maxItem.updatedDate) {
            // Update maxItem if we find an item with a larger updateDate
            maxItem = attachInfo[i]
        }
    }

    return maxItem
}
